import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  en: {
    appName: 'Meet Ethiopians',
    tagline: 'Find your perfect match',
    
    // Auth
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    createAccount: 'Create Account',
    dontHaveAccount: "Don't have an account? Sign up",
    alreadyHaveAccount: 'Already have an account? Sign in',
    
    // Profile Setup
    setupProfile: 'Set Up Your Profile',
    getToKnow: "Let's get to know you better",
    photos: 'Photos',
    dragPhotos: 'Drag photos or click to upload',
    upTo6Photos: 'Up to 6 photos',
    name: 'Name',
    age: 'Age',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    nonBinary: 'Non-binary',
    other: 'Other',
    bio: 'Bio',
    tellAboutYourself: 'Tell us about yourself...',
    interests: 'Interests',
    addInterest: 'Add interest',
    add: 'Add',
    preferences: 'Preferences',
    lookingFor: 'Looking for',
    men: 'Men',
    women: 'Women',
    everyone: 'Everyone',
    minAge: 'Min Age',
    maxAge: 'Max Age',
    continue: 'Continue',
    saving: 'Saving...',
    
    // Discovery
    findingMatches: 'Finding matches...',
    noMoreProfiles: 'No more profiles',
    checkBackLater: 'Check back later for new matches!',
    viewMatches: 'View Your Matches',
    
    // Matches
    yourMatches: 'Your Matches',
    connections: 'connections',
    noMatchesYet: 'No matches yet',
    startSwiping: 'Start swiping to find your match!',
    startSwipingBtn: 'Start Swiping',
    chat: 'Chat',
    
    // Profile
    myProfile: 'My Profile',
    edit: 'Edit',
    about: 'About',
    stats: 'Stats',
    swipes: 'Swipes',
    superLikes: 'Super Likes',
    boosts: 'Boosts',
    logout: 'Logout',
    
    // Subscription
    upgradeExperience: 'Upgrade Your Experience',
    choosePlan: 'Choose a plan that works for you',
    activeSubscription: 'Active Subscription',
    active: 'Active',
    renewsOn: 'Renews on',
    subscriptions: 'Subscriptions',
    boostsAndSuperLikes: 'Boosts & Super Likes',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    save20: 'Save 20%',
    mostPopular: 'Most Popular',
    selectPlan: 'Select Plan',
    processing: 'Processing...',
    buyNow: 'Buy Now',
    paymentMethods: 'Payment Methods',
    comingSoon: 'Coming Soon',
    
    // Subscription Plans
    basic: 'Basic',
    premium: 'Premium',
    vip: 'VIP',
    basicDesc: 'Essential dating features',
    premiumDesc: 'Enhanced matching experience',
    vipDesc: 'Premium experience with exclusive perks',
    
    // Payment
    verifyingPayment: 'Verifying Payment',
    pleaseWait: 'Please wait while we confirm your payment...',
    paymentSuccessful: 'Payment Successful!',
    subscriptionActive: 'Your subscription is now active. Enjoy premium features!',
    purchaseSuccessful: 'Your purchase was successful!',
    amount: 'Amount',
    transactionId: 'Transaction ID',
    paymentProcessing: 'Payment Processing',
    paymentBeingProcessed: "Your payment is being processed. You'll receive a confirmation shortly.",
    paymentFailed: 'Payment Failed',
    unableToVerify: 'Unable to verify your payment. Please contact support if you were charged.',
    paymentNotCompleted: 'Your payment could not be completed. Please try again.',
    tryAgain: 'Try Again',
    goBack: 'Go Back',
    
    // Navigation
    discover: 'Discover',
    matches: 'Matches',
    premiumNav: 'Premium',
    profile: 'Profile',
    
    // Common
    loading: 'Loading...',
    success: 'Success',
    error: 'Error',
    cancel: 'Cancel',
    confirm: 'Confirm',
    online: 'Online'
  },
  am: {
    appName: 'መስተፋቅር',
    tagline: 'ፍጹም የሆነ ጓደኛህን ፈልግ',
    
    // Auth
    email: 'ኢሜይል',
    password: 'የይለፍ ቃል',
    signIn: 'ግባ',
    signUp: 'ተመዝገብ',
    createAccount: 'አካውንት ፍጠር',
    dontHaveAccount: 'አካውንት የለህም? ተመዝገብ',
    alreadyHaveAccount: 'አካውንት አለህ? ግባ',
    
    // Profile Setup
    setupProfile: 'መገለጫህን አዘጋጅ',
    getToKnow: 'የበለጠ እንተዋወቅ',
    photos: 'ፎቶዎች',
    dragPhotos: 'ፎቶዎችን ጎትት ወይም ጫን',
    upTo6Photos: 'እስከ 6 ፎቶዎች',
    name: 'ስም',
    age: 'ዕድሜ',
    gender: 'ጾታ',
    male: 'ወንድ',
    female: 'ሴት',
    nonBinary: 'ሌላ',
    other: 'ሌላ',
    bio: 'ስለራስህ',
    tellAboutYourself: 'ስለራስህ ንገረን...',
    interests: 'ፍላጎቶች',
    addInterest: 'ፍላጎት ጨምር',
    add: 'ጨምር',
    preferences: 'ምርጫዎች',
    lookingFor: 'የምትፈልገው',
    men: 'ወንዶች',
    women: 'ሴቶች',
    everyone: 'ሁሉም',
    minAge: 'ዝቅተኛ ዕድሜ',
    maxAge: 'ከፍተኛ ዕድሜ',
    continue: 'ቀጥል',
    saving: 'በማስቀመጥ ላይ...',
    
    // Discovery
    findingMatches: 'ተስማሚ እየፈለግን...',
    noMoreProfiles: 'ተጨማሪ መገለጫዎች የሉም',
    checkBackLater: 'ለአዲስ ተስማሚዎች በኋላ ተመለስ!',
    viewMatches: 'ተስማሚዎችህን ተመልከት',
    
    // Matches
    yourMatches: 'ተስማሚዎችህ',
    connections: 'ግንኙነቶች',
    noMatchesYet: 'ገና ተስማሚ የለም',
    startSwiping: 'ተስማሚህን ለማግኘት ስዋይፕ ጀምር!',
    startSwipingBtn: 'ስዋይፕ ጀምር',
    chat: 'ውይይት',
    
    // Profile
    myProfile: 'መገለጫዬ',
    edit: 'አርትዕ',
    about: 'ስለ',
    stats: 'ስታቲስቲክስ',
    swipes: 'ስዋይፕ',
    superLikes: 'ሱፐር ላይክ',
    boosts: 'ቡስት',
    logout: 'ውጣ',
    
    // Subscription
    upgradeExperience: 'ተሞክሮህን አሻሽል',
    choosePlan: 'ለአንተ የሚሰራ እቅድ ምረጥ',
    activeSubscription: 'ንቁ ምዝገባ',
    active: 'ንቁ',
    renewsOn: 'የሚታደስበት',
    subscriptions: 'ምዝገባዎች',
    boostsAndSuperLikes: 'ቡስት እና ሱፐር ላይክ',
    monthly: 'ወርሃዊ',
    quarterly: 'ሩብ አመት',
    yearly: 'አመታዊ',
    save20: '20% ቁጠብ',
    mostPopular: 'በጣም ተወዳጅ',
    selectPlan: 'እቅድ ምረጥ',
    processing: 'በማስኬድ ላይ...',
    buyNow: 'አሁን ግዛ',
    paymentMethods: 'የመክፈያ መንገዶች',
    comingSoon: 'በቅርቡ',
    
    // Subscription Plans
    basic: 'መሰረታዊ',
    premium: 'ፕሪሚየም',
    vip: 'ቪአይፒ',
    basicDesc: 'መሰረታዊ ባህሪያት',
    premiumDesc: 'የላቀ ተሞክሮ',
    vipDesc: 'ልዩ ፕሪሚየም ተሞክሮ',
    
    // Payment
    verifyingPayment: 'ክፍያ በማረጋገጥ ላይ',
    pleaseWait: 'ክፍያህን እያረጋገጥን እባክህ ጠብቅ...',
    paymentSuccessful: 'ክፍያ በተሳካ ሁኔታ ተከናውኗል!',
    subscriptionActive: 'ምዝገባህ አሁን ንቁ ነው። ፕሪሚየም ባህሪያትን ተጠቀም!',
    purchaseSuccessful: 'ግዢህ በተሳካ ሁኔታ ተከናውኗል!',
    amount: 'መጠን',
    transactionId: 'የግብይት መለያ',
    paymentProcessing: 'ክፍያ በሂደት ላይ',
    paymentBeingProcessed: 'ክፍያህ በሂደት ላይ ነው። በቅርቡ ማረጋገጫ ይደርስሃል።',
    paymentFailed: 'ክፍያ አልተሳካም',
    unableToVerify: 'ክፍያህን ማረጋገጥ አልተቻለም። ከተከፈለህ ድጋፍን አግኝ።',
    paymentNotCompleted: 'ክፍያህ ሊጠናቀቅ አልቻለም። እባክህ እንደገና ሞክር።',
    tryAgain: 'እንደገና ሞክር',
    goBack: 'ተመለስ',
    
    // Navigation
    discover: 'አግኝ',
    matches: 'ተስማሚዎች',
    premiumNav: 'ፕሪሚየም',
    profile: 'መገለጫ',
    
    // Common
    loading: 'በመጫን ላይ...',
    success: 'ተሳክቷል',
    error: 'ስህተት',
    cancel: 'ሰርዝ',
    confirm: 'አረጋግጥ',
    online: 'መስመር ላይ'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language][key] || translations['en'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'am' : 'en');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isAmharic: language === 'am'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};